import React from 'react';
import {
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import StatusChip from '../../../../../components/Billing/Invoice/InvoiceStatusChip';
import { PackageConsumptionItem } from './consumptionDetails.helper';
import { safeConvertUnderscoreToTitle, toTitleCase } from '../../../../../utils/commonUtils';
import useCurrency from '../../../../../components/CurrencyProvider';

// ============================================================================
// Types
// ============================================================================

export interface ConsumptionTableProps {
  /** Filtered items to display in the table */
  items: PackageConsumptionItem[];
}

// ============================================================================
// Component
// ============================================================================

const ConsumptionTable: React.FC<ConsumptionTableProps> = ({
  items,
}) => {
  const { t } = useTranslation('Packagebilling');
  const { currencySymbol } = useCurrency();

  /** Check if a row has no pending quantity (exhausted) */
  const isRowExhausted = (item: PackageConsumptionItem) =>
    item.pending_quantity === 0 && !item.unlimited_quantity;

  return (
    <TableContainer
      component={Paper}
      data-testid="consumption-table-container"
      sx={{
        minHeight: 200,
        boxShadow: 'none',
        border: 'none',
        borderRadius: 0,
        overflow: 'auto',
      }}
    >
      <Table
        data-testid="consumption-table"
        stickyHeader
        sx={{ borderCollapse: 'collapse', minWidth: 800 }}
        aria-label={t('consumption_details.table_aria')}
      >
        <TableHead>
          <TableRow
            sx={{
              background: '#F5F6F7',
              '& .MuiTableCell-root': { 
                borderBottom: 'none',
                background: '#F5F6F7',
              },
              whiteSpace: 'nowrap',
            }}
          >
            <TableCell
              sx={{
                borderTopLeftRadius: 12,
                borderBottomLeftRadius: 12,
              }}
            >
              {t('consumption_details.service_name')}
            </TableCell>
            <TableCell>{t('consumption_details.procedure_name')}</TableCell>
            <TableCell>{t('consumption_details.type')}</TableCell>
            <TableCell>{t('consumption_details.gender')}</TableCell>
            <TableCell>{t('consumption_details.discount_percent')}</TableCell>
            <TableCell>{`${t('consumption_details.unit_rate')} (${currencySymbol})`}</TableCell>
            <TableCell>{t('consumption_details.quantity')}</TableCell>
            <TableCell>{t('consumption_details.used')}</TableCell>
            <TableCell>{t('consumption_details.pending')}</TableCell>
            <TableCell>{t('consumption_details.discount_amount')}</TableCell>
            <TableCell>{t('consumption_details.net_payable')}</TableCell>
            <TableCell>{t('consumption_details.unlimited_quantity')}</TableCell>
            <TableCell>{t('consumption_details.adjustable_head')}</TableCell>
            <TableCell
              sx={{
                borderTopRightRadius: 12,
                borderBottomRightRadius: 12,
              }}
            >
              {t('consumption_details.adjustable_head_type')}
            </TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {items.length === 0 ? (
            <TableRow>
              <TableCell colSpan={14} align="center">
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ py: 3, fontSize: 13 }}
                >
                  {t('consumption_details.empty')}
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            items.map((item, idx) => {
              const isExhausted = isRowExhausted(item);

              return (
                <TableRow
                  key={`${item.id}_${idx}`}
                  data-testid={`consumption-table-row-${idx}`}
                  sx={{
                    background: '#FFFFFF',
                    height: 56,
                    '& td': {
                      borderTop: idx === 0 ? 'none' : undefined,
                      borderBottom: idx === items.length - 1 ? 'none' : undefined,
                    },
                  }}
                >
                  <TableCell
                    sx={{
                      borderTopLeftRadius: 12,
                      borderBottomLeftRadius: 12,
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {item.item_name}
                    </Typography>
                  </TableCell>
                  <TableCell>{ safeConvertUnderscoreToTitle(item.procedure_name) || '-'}</TableCell>
                  <TableCell>
                    <StatusChip
                      status={item.item_type}
                      height={22}
                      width={95}
                      sx={{ fontSize: '11px', px: 0.5, py:1, minWidth: 'unset' }}
                    />
                  </TableCell>
                  <TableCell>{ toTitleCase(item.gender) || '-'}</TableCell>
                  <TableCell>{item.discount_percent.toFixed(2)}</TableCell>
                  <TableCell>
                    {item.unlimited_quantity
                      ? '0.00'
                      : item.unit_rate.toFixed(2)}
                  </TableCell>
                  <TableCell>{item.total_quantity}{item.uom ? ` ${item.uom}` : ''}</TableCell>
                  <TableCell>{item.availed_quantity}{item.uom ? ` ${item.uom}` : ''}</TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: 13,
                        color: isExhausted ? 'error.main' : 'inherit',
                      }}
                    >
                      {item.pending_quantity}{item.uom ? ` ${item.uom}` : ''}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {item.unlimited_quantity
                      ? '0.00'
                      : ((item.unit_rate * item.discount_percent) / 100).toFixed(2)}
                  </TableCell>
                  <TableCell>
                    {item.unlimited_quantity
                      ? '0.00'
                      : (item.unit_rate * (1 - item.discount_percent / 100)).toFixed(2)}
                  </TableCell>
                  <TableCell>
                    {item.unlimited_quantity ? t('consumption_details.unlimited') : '-'}
                  </TableCell>
                  <TableCell>
                    {item.adjustable_head ? t('consumption_details.yes') : '-'}
                  </TableCell>
                  <TableCell
                    sx={{
                      borderTopRightRadius: 12,
                      borderBottomRightRadius: 12,
                    }}
                  >
                    {item.adjustable_head_type === 'pharmacy' 
                      ? t('consumption_details.item_type_pharmacy') 
                      : item.adjustable_head_type === 'clinical' 
                        ? t('consumption_details.clinical') 
                        : '-'}
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default ConsumptionTable;
